import React from 'react';
import {
  Zap,
  CheckCircle,
  Search,
  Users,
  Award,
  Globe,
  Rocket,
  ArrowRight,
} from 'lucide-react';

import { motion } from 'framer-motion';

const fadeInVariant = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Solution = () => {
  const solutions = [
    {
      icon: <Zap className="w-16 h-16 text-yellow-600" />,
      title: "Efficient Screening",
      description: "AI-powered evaluation reduces screening time by up to 70%, letting you focus on top candidates.",
      features: [
        "Smart resume parsing and keyword matching",
        "Automated skills assessment and scoring",
        "Pre-screening questionnaire automation"
      ],
      stats: {
        value: "70%",
        label: "Time Saved in Screening"
      },
      additionalInfo: "Our AI screening technology learns from your successful hires to continuously improve candidate matching and reduce time-to-hire metrics."
    },
    {
      icon: <CheckCircle className="w-16 h-16 text-green-600" />,
      title: "Objective Decision Making",
      description: "Data-driven assessments minimize unconscious bias and improve hiring fairness.",
      features: [
        "Standardized evaluation criteria",
        "Blind screening options",
        "Diversity and inclusion metrics"
      ],
      stats: {
        value: "89%",
        label: "More Diverse Candidate Pool"
      },
      additionalInfo: "Make confident hiring decisions backed by data-driven insights and structured evaluation frameworks."
    },
    {
      icon: <Search className="w-16 h-16 text-purple-600" />,
      title: "Uncover Hidden Talent",
      description: "Advanced matching algorithms reveal potential in candidates often overlooked by traditional methods.",
      features: [
        "Skills-based candidate matching",
        "Potential assessment tools",
        "Career trajectory analysis"
      ],
      stats: {
        value: "45%",
        label: "More Qualified Candidates"
      },
      additionalInfo: "Look beyond traditional qualifications to find candidates with the right potential and transferable skills."
    },
    {
      icon: <Users className="w-16 h-16 text-indigo-600" />,
      title: "Team Collaboration",
      description: "Facilitate teamwork with shared evaluations, interview feedback, and collaborative decision-making.",
      features: [
        "Real-time feedback sharing",
        "Collaborative hiring workflows",
        "Team evaluation dashboards"
      ],
      stats: {
        value: "3x",
        label: "Faster Decision Making"
      },
      additionalInfo: "Keep your entire hiring team aligned with transparent communication and structured collaboration tools."
    },
    {
      icon: <Award className="w-16 h-16 text-blue-600" />,
      title: "Candidate Assessments",
      description: "Evaluate skills, emotional intelligence, and cultural fit to ensure a well-rounded hiring decision.",
      features: [
        "Custom assessment creation",
        "Skill-specific testing",
        "Behavioral analysis tools"
      ],
      stats: {
        value: "92%",
        label: "Hiring Success Rate"
      },
      additionalInfo: "Create comprehensive candidate profiles that go beyond technical skills to ensure long-term success."
    },
    {
      icon: <Globe className="w-16 h-16 text-red-600" />,
      title: "Global Reach",
      description: "Seamless integrations with platforms like Gmail, LinkedIn, and Zoom make remote hiring effortless.",
      features: [
        "Multi-language support",
        "Time zone management",
        "Virtual interview tools"
      ],
      stats: {
        value: "150+",
        label: "Countries Supported"
      },
      additionalInfo: "Break down geographical barriers and build diverse teams across the globe with our comprehensive remote hiring toolkit."
    }
  ];

  return (
    <div className="pt-16">
      {/* Header Section */}
      
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="text-center max-w-3xl mx-auto py-20 px-4">
          <p className="mt-2 text-4xl font-extrabold sm:text-5xl">Addressing Recruitment Challenges</p>
          <p className="mt-6 text-xl text-blue-100">Our platform solves critical pain points in recruitment with AI-driven solutions tailored to modern hiring needs.</p>
        </div>
      </div>
      {/* Solutions Section */}
      <div className="w-full">
        {solutions.map((solution, index) => (
          <motion.div
            key={index}
            className={`py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`}
            variants={fadeInVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-start gap-12`}>
                {/* Icon and Stats Section */}
                <div className="lg:w-1/3 flex flex-col items-center">
                  <motion.div
                    className="p-8 rounded-full bg-white shadow-lg mb-8"
                    variants={fadeInVariant}
                  >
                    {solution.icon}
                  </motion.div>
                  <motion.div
                    className="text-center p-6 bg-gray-50 rounded-lg w-full"
                    variants={fadeInVariant}
                  >
                    <div className="text-4xl font-bold text-blue-600">{solution.stats.value}</div>
                    <div className="text-sm text-gray-600 mt-2">{solution.stats.label}</div>
                  </motion.div>
                </div>

                {/* Description and Features Section */}
                <div className="lg:w-2/3 text-center lg:text-left">
                  <motion.h3
                    className="text-3xl font-bold text-gray-900 mb-4"
                    variants={fadeInVariant}
                  >
                    {solution.title}
                  </motion.h3>
                  <motion.p
                    className="text-xl text-gray-600 mb-6"
                    variants={fadeInVariant}
                  >
                    {solution.description}
                  </motion.p>
                  <motion.p
                    className="text-gray-600 mb-8"
                    variants={fadeInVariant}
                  >
                    {solution.additionalInfo}
                  </motion.p>
                  <motion.div
                    className="bg-white rounded-lg p-6 shadow-sm"
                    variants={fadeInVariant}
                  >
                    <h4 className="font-semibold text-gray-900 mb-4">Key Features:</h4>
                    <ul className="space-y-3">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-gray-600">
                          <ArrowRight className="w-5 h-5 text-blue-600 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA Section */}
      <motion.div
        className="bg-blue-600 py-16"
        variants={fadeInVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
                Transform Your Recruitment Today
              </h2>
              <p className="mt-4 text-xl text-blue-100">
                Experience a smarter, faster, and fairer hiring process with our AI-powered platform. Start your journey now.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="/demo"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg border-2 border-white text-white hover:bg-blue-700 font-medium transition-colors duration-300"
                >
                  Request Demo
                  <Rocket className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Solution;